
import Vue, { VueConstructor } from "vue"
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import dateFormatMixin from '@/mixins/DateFormatMixin.vue'

interface Timer extends HTMLFormElement {
  selectingHour: boolean
}

interface DateTimeType {
  dateObject: Date | string,
  dateString: string,
  timeString: string
}

const DEFAULT_DATE = ''
const DEFAULT_TIME = '00:00:00'
const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd'
const DEFAULT_TIME_FORMAT = 'HH:mm:ss'
const DEFAULT_DIALOG_WIDTH = 340

export default (Vue as VueConstructor<Vue & InstanceType<typeof dateFormatMixin>>).extend({
  name: 'DateTimePicker',
  model: {
    prop: 'datetime',
    event: 'input'
  },
  mixins: [
    dateFormatMixin
  ],
  props: {
    datetime: {
      // type: DateTimeType,
      default: {
        dateObject: "",
        dateString: "",
        timeString: ""
      } as DateTimeType
    },
    disabled: {
      type: Boolean
    },
    loading: {
      type: Boolean
    },
    label: {
      type: String,
      default: ''
    },
    dialogWidth: {
      type: Number,
      default: DEFAULT_DIALOG_WIDTH
    },
    dateFormat: {
      type: String,
      default: DEFAULT_DATE_FORMAT
    },
    timeFormat: {
      type: String,
      default: 'HH:mm'
    },
    textFieldProps: {
      type: Object
    },
    datePickerProps: {
      type: Object
    },
    timePickerProps: {
      type: Object
    },
    required: {
      type: Boolean,
      default: false
    },
    classStyle: {
      type: String,
      default: "mr-2"
    },
    width: {
      type: String,
      default: "200"
    }
  },
  data() {
    return {
      display: false,
      activeTab: 0,
      date: DEFAULT_DATE,
      time: DEFAULT_TIME,
      dateFormatted: null as null | string,
    }
  },
  mounted() {
    this.init()
    this.$emit('input', {
      dateObject: this.selectedDatetime,
      dateString: this.date,
      timeString: this.time
    })
  },
  computed: {
    dateTimeFormat(): string {
      return this.dateFormat + ' ' + this.timeFormat
    },
    defaultDateTimeFormat(): string {
      return DEFAULT_DATE_FORMAT + ' ' + DEFAULT_TIME_FORMAT
    },
    formattedDatetime(): string {
      return this.selectedDatetime ? this.localizeDate(`${this.date} ${this.time}`, true, true).substring(0,16) : ''
    },
    selectedDatetime(): Date | null {
      if (this.date && this.time) {
        let datetimeString = this.date + ' ' + this.time
        if (this.time.length === 5) {
          datetimeString += ':00'
        }
        return parse(datetimeString, this.defaultDateTimeFormat, new Date())
      } else {
        return null
      }
    },
    dateSelected(): boolean {
      return !this.date
    },
    requiredRule(): boolean | ((value: string) => boolean | string) {
      if (this.required) {
        return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.required_field')
      }
      return true
    },
  },
  methods: {
    init() {
      if (!this.datetime.dateObject) {
        return
      }

      let initDateTime
      if (this.datetime.dateObject instanceof Date) {
        initDateTime = this.datetime.dateObject
        this.date = format(initDateTime, DEFAULT_DATE_FORMAT)
        this.time = format(initDateTime, DEFAULT_TIME_FORMAT).slice(0, 5)
      } else if (typeof this.datetime.dateObject === 'string' || String instanceof this.datetime.dateObject) {
        // see https://stackoverflow.com/a/9436948
        initDateTime = parse(this.datetime.dateObject, this.dateTimeFormat, new Date())
        this.date = format(initDateTime, DEFAULT_DATE_FORMAT)
        this.time = format(initDateTime, DEFAULT_TIME_FORMAT).slice(0, 5)
      }
    },
    okHandler() {
      this.resetPicker()
      this.$emit('input', {
        dateObject: this.selectedDatetime,
        dateString: this.date,
        timeString: this.time,
        updated: true // We tag the date as updated when value has changed
      })
    },
    clearHandler() {
      this.resetPicker()
      this.init()
    },
    resetPicker() {
      this.display = false
      this.activeTab = 0
      if (this.$refs.timer) {
        (this.$refs.timer as Timer).selectingHour = true
      }
    },
    showTimePicker() {
      this.activeTab = 1
    }
  },
  watch: {
    datetime: function () {
      this.init()
    }
  }
})
